import { SignalrBaseService } from '@abb-procure/api';
import { DialogService, FingerprintService } from '@abb-procure/common';
import { ErrorHandlerService } from '@abb-procure/error-handling';
import { SnackbarService } from '@abb-procure/snackbar';
import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalService } from '@azure/msal-angular';
import type { HubConnection } from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { SessionsDialogService } from './sessions-dialog.service';

const LOGOUT_TIMEOUT = 10000;

enum MessageKeys {
  NotificationsKey = 'Notifications',
  TerminatedSessionsKey = 'TerminatedSessions',
  ConcurrentSessionsKey = 'ConcurrentSessions',
  ExpiredSessionsKey = 'ExpiredSessions',
  WarningExpirationSessionsKey = 'WarningExpirationSessions',
}

@Injectable({
  providedIn: 'root',
})
export class SignalrService extends SignalrBaseService {
  readonly messagesSubject$ = new BehaviorSubject<string | null>(null);
  readonly messages$ = this.messagesSubject$.asObservable();

  protected override logPrefix = 'SignalR';

  private userIsNavigatingAway = false;
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly accessService = inject(MsalService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly sessionsDialogService = inject(SessionsDialogService);
  private readonly dialogService = inject(DialogService);
  private readonly fingerprintService = inject(FingerprintService);

  connect(): void {
    if (!this.environmentConfig.production) {
      return;
    }

    void this.initializeConnection();
  }

  async disconnectHub(): Promise<void> {
    this.userIsNavigatingAway = true;

    await super.stopConnection();
    this.log('Connection stopped');
  }

  private async initializeConnection(): Promise<void> {
    try {
      await super.startConnection(
        {
          url: `${this.environmentConfig.apiEndpoint}application?Fingerprint=${this.fingerprintService.fingerprint}`,
          headers: {
            Fingerprint: this.fingerprintService.fingerprint,
          },
        },
        (hub) => {
          this.registerHandlers(hub);
        },
      );

      this.log('Connection started');
    } catch (error) {
      this.errorHandlerService.trackError(error);
    }
  }

  private registerHandlers(hub: HubConnection): void {
    this.listen(hub);

    hub.onreconnected(() => {
      this.log('Reconnected');
    });

    hub.onclose(() => {
      if (this.userIsNavigatingAway) {
        return;
      }

      void this.initializeConnection();
      this.log('Disconnected, attempting to reconnect');
    });
  }

  private listen(hub: HubConnection): void {
    hub.on(MessageKeys.NotificationsKey, () => this.handleNotifications());
    hub.on(MessageKeys.ConcurrentSessionsKey, () =>
      this.handleConcurrentSessions(),
    );
    hub.on(MessageKeys.ExpiredSessionsKey, () => this.handleExpiredSessions());
    hub.on(MessageKeys.TerminatedSessionsKey, () =>
      this.handleTerminatedSessions(),
    );
    hub.on(MessageKeys.WarningExpirationSessionsKey, () =>
      this.handleWarningExpirationSessions(),
    );
  }

  private handleNotifications(): void {
    this.handleMessage(MessageKeys.NotificationsKey);
  }

  private handleTerminatedSessions(): void {
    this.handleMessage(MessageKeys.TerminatedSessionsKey);

    const dialog = this.dialogService.showOkDialog({
      title: 'Session Terminated',
      text: 'Your session has been terminated since there is another active session.',
    });

    dialog
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.logout();
      });

    setTimeout(() => {
      this.logout();
    }, LOGOUT_TIMEOUT);
  }

  private handleExpiredSessions(): void {
    this.handleMessage(MessageKeys.ExpiredSessionsKey);

    const dialog = this.dialogService.showOkDialog({
      title: 'Session Expired',
      text: 'Your session has expired. Please login again.',
    });

    dialog
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.logout();
      });

    setTimeout(() => {
      this.logout();
    }, LOGOUT_TIMEOUT);
  }

  private handleConcurrentSessions(): void {
    this.handleMessage(MessageKeys.ConcurrentSessionsKey);

    this.snackbarService.showInfoSnackbar({
      text: 'You have logged in from another device.',
    });
  }

  private handleWarningExpirationSessions(): void {
    this.handleMessage(MessageKeys.WarningExpirationSessionsKey);
    this.sessionsDialogService.openRenewDialog();
  }

  private logout(): void {
    this.accessService.logoutRedirect({
      account: this.accessService.instance.getActiveAccount(),
    });
  }

  private handleMessage(message: string): void {
    this.messagesSubject$.next(message);
    this.log(`New message "${message}"`);
  }
}
